import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { fetchWithAuth } from '../../libs/apiUtils'
import Modal from '../Modal'

interface UpdatePasswordRequest {
  id?: string
  password?: string
  passwordConfirmation?: string
}

interface UpdatePasswordModalProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  request: UpdatePasswordRequest
}

export default function UpdatePasswordModal({
  isOpen,
  setIsOpen,
  request,
}: UpdatePasswordModalProps) {
  const [formData, setFormData] = useState<UpdatePasswordRequest>({
    ...request,
    password: '',
    passwordConfirmation: '',
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (request) {
      setFormData({
        id: request.id,
        password: request.password || '',
        passwordConfirmation: request.passwordConfirmation || '',
      })
    }
  }, [request])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined

    setFormData(prev => {
      return {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }
    })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!formData.password?.trim()) return toast.error('Password cannot be empty')
    if (!formData.passwordConfirmation?.trim()) return toast.error('Password confirmation cannot be empty')
    if (!(formData.passwordConfirmation?.trim() === formData.password?.trim())) return toast.error('Password confirmation is not the same, please check your input again')
    if (!formData.id) return toast.error('No user selected')

    try {
      setLoading(true)
      const res = await fetchWithAuth(
        `${process.env.REACT_APP_BACKEND_URL}/api/user-management/password`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      )

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data[0].message || 'Failed updating password')
        return
      }

      toast.success('Password updated successfully')
      setIsOpen(false)
    } catch (err) {
      toast.error('Network error, check your connection')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <h3 className="text-lg font-semibold mb-4">Ganti Password</h3>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1">
            Password
          </label>
          <input
            type="password"
            id="password"
            name="password"
            className="border px-3 py-2 rounded w-full"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-4">
          <label htmlFor="passwordConfirmation" className="block text-sm font-medium text-neutral-700 mb-1">
            Konfirmasi Password
          </label>
          <input
            type="password"
            id="passwordConfirmation"
            name="passwordConfirmation"
            className="border px-3 py-2 rounded w-full"
            value={formData.passwordConfirmation}
            onChange={handleChange}
            required
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="px-4 py-2 text-neutral-700 rounded hover:bg-neutral-100"
            onClick={() => setIsOpen(false)}
            disabled={loading}
          >
            Batal
          </button>
          <button
            type="submit"
            className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
